/**
 * กระบวนท่าที่ยังไม่ได้เรียน (locked moves): a new character starts with a few moves of its art (data/moveUnlocks.ts) and
 * learns the rest one at a time from a master.
 *
 * - **State.** `progress.movesLearned`: the moves learned so far. Absent (older saves, worlds that never gated), every
 *   move of a learned art is usable, as before. `startMoveGate` turns the gate on for a new character.
 * - **Locked moves** stay on the bar (shown locked) but cannot be used: the `skill` command on a locked slot is
 *   refused, and the auto-battle leaves them out (`moveLocked`, read by systems/skillBar.ts).
 * - `learnMove` (`{ id }`, or `{ all: true }` for the dev key) unlocks a move and emits `moveLearned`. The quest chain
 *   calls it; it is never accepted from the network (net/protocol.ts).
 */
import { MOVE_UNLOCKS, moveLocked } from '../data/moveUnlocks';
import { getArt, SKILLS } from '../data/skills';
import type { PlayerProgress } from '../progress';
import type { PlayerCommand } from '../world';
import { skillBarOf } from './skillBar';
import type { SystemHost, WorldSystem } from './types';

/** Commands (part of `PlayerCommand`). */
export type MoveLocksCommand = { t: 'learnMove'; id?: string; all?: boolean };

/** Events (part of `WorldEvent`). */
export type MoveLocksEvent = { type: 'moveLearned'; skillId: string };

export { moveLocked };

type LockProgress = Pick<PlayerProgress, 'movesLearned'>;

/** Turns the gate on for a new character: only the art's starting moves usable (all of them for an art without a list). */
export function startMoveGate(pr: Pick<PlayerProgress, 'movesLearned' | 'activeArt'>): void {
  const art = pr.activeArt;
  pr.movesLearned = [...(MOVE_UNLOCKS[art]?.start ?? getArt(art).skills)];
}

/** Learns a move (true when it was locked). */
export function learnMove(pr: LockProgress, id: string): boolean {
  if (!SKILLS[id] || !moveLocked(pr, id)) return false;
  pr.movesLearned!.push(id);
  return true;
}

/** A saved list, checked on load: known move ids only, each once; anything else (absent included) is no gate. */
export function sanitizeMovesLearned(v: unknown): string[] | undefined {
  if (!Array.isArray(v)) return undefined;
  return [...new Set(v.filter((id): id is string => typeof id === 'string' && !!SKILLS[id]))];
}

export class MoveLocksSystem implements WorldSystem {
  readonly name = 'moveLocks';

  constructor(private readonly h: SystemHost) {}

  readonly commands = {
    learnMove: (c: MoveLocksCommand) => this.learn(c),
  };

  /** A locked move's slot does nothing. */
  refuse(cmd: PlayerCommand): boolean {
    if (cmd.t !== 'skill') return false;
    const pr = this.h.world.progress;
    const id = skillBarOf(pr)[cmd.slot];
    return !!id && moveLocked(pr, id);
  }

  private learn(c: MoveLocksCommand): void {
    const pr = this.h.world.progress as PlayerProgress;
    if (!pr.movesLearned) return;
    if (c.all) pr.movesLearned = undefined;
    else if (c.id && learnMove(pr, c.id)) this.h.emit({ type: 'moveLearned', skillId: c.id });
    else return;
    this.h.emit({ type: 'progressChanged' });
  }
}
